/**
 * Clear Filters Functionality
 *
 * Handles clearing all filter parameters from the URL and triggering a facet update
 */

(function () {
  "use strict";

  // Wait for DOM to be ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    const productCards = document.querySelectorAll("product-card");
    productCards.forEach((card) => {
      card.addEventListener(
        "click",
        (event) =>
          handleWishlistEvent(
            event,
            event.target.closest(".swym-wishlist-collections-v2"),
          ),
        true,
      );
    });

    document.querySelectorAll(".swym-add-to-wishlist").forEach((item) => {
      item.addEventListener(
        "click",
        (event) => handleWishlistEvent(event, item),
        true,
      );
    });
  }

  function handleWishlistEvent(event, el) {
    if (!el) {
      return;
    }

    if (
      document.querySelector("body")?.getAttribute("data-customer") === "true"
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    document.getElementById("account")?.show();
  }
})();

(function () {
  "use strict";

  const DRAWERS = new Set(["login", "register", "account"]);
  const STORAGE_KEY = "jkl-open-drawer";
  const DRAWER_LINK_SELECTOR =
    'a[href="#login"], a[href="#register"], a[href="#account"]';
  // Consent UIs that may render on top of an open drawer (Cookiebot bottom bar,
  // Pandectes banner). Taps on these must pass through the drawer's focus trap.
  const COOKIE_CONSENT_SELECTOR = [
    "#CybotCookiebotDialog",
    "#CybotCookiebotDialogBodyUnderlay",
    ".CybotCookiebotDialog",
    ".cc-window",
    ".cc-window-wrapper",
    '[id^="pandectes"]',
  ].join(", ");

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    document.addEventListener("click", onDrawerLinkClick, true);
    window.addEventListener("hashchange", onDrawerHashChange);
    trackDismissSignals();

    const params = new URLSearchParams(location.search);
    const pending = consumePendingDrawer() ?? getDrawerHashFromUrl();

    customElements.whenDefined("x-drawer").then(() => {
      document.getElementById("account")?.removeAttribute("open");

      DRAWERS.forEach((id) => {
        const drawer = document.getElementById(id);
        if (!drawer) return;

        drawer.addEventListener("dialog:after-hide", stripDrawerHash);
        hardenDrawer(drawer);
      });

      if (params.get("open") === "register") {
        openDrawer("register");
      } else if (params.get("return_to")) {
        const input = document.querySelector(
          'x-drawer#login input[name="checkout_url"]',
        );
        if (input) input.value = params.get("return_to");
        openDrawer("login");
      } else if (pending) {
        openDrawer(pending);
        stripDrawerHash();
      }
    });
  }

  // The deepest element the last pointer interaction actually started on. Used to
  // distinguish a genuine backdrop tap from a "phantom" click produced when a
  // layer on top of the drawer is removed mid-click.
  let lastPointerDownTarget = null;

  function trackDismissSignals() {
    document.addEventListener(
      "pointerdown",
      (event) => {
        lastPointerDownTarget = event.composedPath?.()[0] ?? event.target;
      },
      true,
    );
  }

  /**
   * The x-drawer (DialogElement) can be dismissed through two "outside" paths:
   *   1. its overlay backdrop click handler (calls hide() directly), and
   *   2. the focus-trap "click outside" behavior.
   *
   * The cookie banner (Pandectes, z-index 2147483647) and promotional popups are
   * rendered on top of the drawer. Interacting with them (accept/reject cookies,
   * close a promo) is treated as an outside click; worse, when the banner removes
   * itself mid-click the resulting click retargets onto the drawer overlay and
   * closes it via path #1, even though the user never touched the drawer.
   *
   * We close path #2 by disabling the focus-trap outside-click hide, and we guard
   * path #1 with a capture-phase listener on the host (which runs before the
   * deeper overlay listener) so the backdrop only closes the drawer when the
   * press genuinely started on the backdrop itself. The drawer's own close
   * buttons keep working since they don't go through these paths.
   *
   * Additionally, on touch devices focus-trap's outside-click handler defaults to
   * preventDefault-ing taps that land outside the drawer (allowOutsideClickForTarget
   * returns false), so tapping "accept/reject" on the cookie bar never reaches
   * Cookiebot and the sticky bottom bar can't be dismissed. On desktop the mouse
   * path lets those clicks through, which is why the bug is mobile-only. We let
   * consent-UI taps pass through here; the drawer still won't close thanks to the
   * hideForOutsideClickTarget override above.
   */
  function hardenDrawer(drawer) {
    drawer.hideForOutsideClickTarget = () => false;
    drawer.allowOutsideClickForTarget = (target) =>
      Boolean(target?.closest?.(COOKIE_CONSENT_SELECTOR));

    const overlay = drawer.shadowRoot?.querySelector('[part="overlay"]');
    if (!overlay) return;

    drawer.addEventListener(
      "click",
      (event) => {
        const path = event.composedPath?.() ?? [];
        if (!path.includes(overlay)) return;
        if (lastPointerDownTarget !== overlay) {
          event.stopImmediatePropagation();
        }
      },
      true,
    );
  }

  function onDrawerLinkClick(event) {
    const link = event.target.closest(DRAWER_LINK_SELECTOR);
    if (
      !link ||
      link.pathname !== location.pathname ||
      link.search !== location.search
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    openDrawer(link.hash.slice(1));
  }

  function onDrawerHashChange() {
    const hash = location.hash.slice(1);
    if (!DRAWERS.has(hash)) return;
    openDrawer(hash);
    stripDrawerHash();
  }

  function consumePendingDrawer() {
    try {
      const pending = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
      return DRAWERS.has(pending) ? pending : null;
    } catch {
      return null;
    }
  }

  function getDrawerHashFromUrl() {
    const hash = location.hash.slice(1);
    return DRAWERS.has(hash) ? hash : null;
  }

  function stripDrawerHash() {
    if (!DRAWERS.has(location.hash.slice(1))) return;
    history.replaceState(null, "", location.pathname + location.search);
  }

  function openDrawer(id) {
    if (id === "register") {
      initRegister();
      return;
    }

    document.getElementById(id)?.show();
    window.scrollTo(0, 0);
  }

  function initRegister() {
    const form = document.getElementById("register-form");
    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get("ref-id");
    const normalizedRefId = refId?.trim().toLowerCase();

    document.getElementById("register")?.show();
    window.scrollTo(0, 0);

    if (!form) {
      console.warn("Register form not found");
      return;
    }

    if (!normalizedRefId) {
      return;
    }

    fetchData(getFormId(form))
      .then((data) => {
        if (!data) {
          throw new Error("Invalid form data received");
        }

        const { storeFieldId, storeFieldEnum } = getStoreKey(
          data,
          normalizedRefId,
        );
        formLoaded(form, () => {
          selectStoreOption(storeFieldEnum, storeFieldId);
        });
      })
      .catch((err) => {
        console.error("Failed to initialize register form:", err);
      });
  }

  function getStoreKey(storeData, refId) {
    const { revision } = storeData;

    const storeField = revision.fields.find((field) => {
      const key = field.dataColumn.key;

      return key === "your_favorite_store";
    });

    if (!storeField) {
      // Warning error log
      console.warn('Field "your_favorite_store" not found in form schema');

      return {
        storeFieldId: null,
        storeFieldEnum: null,
      };
    }

    const storeFieldEnum = storeField?.enum?.find((option) => {
      const optionValue = option?.value?.trim().toLowerCase();

      return optionValue === refId;
    });

    return {
      storeFieldId: storeField.id,
      storeFieldEnum,
    };
  }

  function selectStoreOption(storeFieldEnum, storeFieldId) {
    if (!storeFieldEnum) {
      // Warning error log
      console.warn("No matching store option found for provided ref-id");
      return;
    }

    const parent = document.querySelector(
      `[data-cf-field-id="${storeFieldId}"]`,
    );

    if (!parent) {
      console.warn(`Store field with ID ${storeFieldId} not found`);
      return;
    }

    const button = [...parent.querySelectorAll('.cf-select-search-result')]
      .find(el => el.textContent.trim() === storeFieldEnum.label);

    button?.click();
  }

  function getFormId(form) {
    return form?.getAttribute("data-cf-form") || null;
  }

  function formLoaded(form, cb) {
    const ATTRIBUTE_NAME = "data-cf-state";
    const TIMEOUT = 5000; // 5 seconds

    const timeoutId = setTimeout(() => {
      observer.disconnect();
      console.warn("Form loading timeout");
    }, TIMEOUT);

    const observer = new MutationObserver((mutations) => {
      for (const { attributeName } of mutations) {
        if (attributeName === ATTRIBUTE_NAME) {
          const value = form.getAttribute(ATTRIBUTE_NAME);
          if (value === "ready") {
            clearTimeout(timeoutId);
            cb();
            observer.disconnect();
          }
        }
      }
    });

    if (form.getAttribute(ATTRIBUTE_NAME) === "ready") {
      clearTimeout(timeoutId);
      cb();
      return;
    } else {
      observer.observe(form, {
        attributes: true,
        attributeFilter: [ATTRIBUTE_NAME],
      });
    }
  }

  async function fetchData(formId) {
    try {
      const response = await fetch(
        `https://app.customerfields.com/embed_api/v4/forms/${formId}.json`,
        {
          headers: {
            Accept: "*/*",
            "X-Shopify-Shop-Domain": "danjohn-it.myshopify.com",
          },
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  }
})();

/**
 * Cart drawer clear button
 */
(function () {
  "use strict";

  const CLEAR_BUTTON_SELECTOR = "[data-clear-cart-button]";
  const DEFAULT_ERROR = "Unable to clear cart.";
  let listenersInitialized = false;
  let isClearingCart = false;
  const initializedDrawers = new WeakSet();
  let currentCartItemCount = null;

  function initClearCartButton() {
    if (listenersInitialized) {
      return;
    }

    listenersInitialized = true;
    document.querySelectorAll("cart-drawer").forEach(initializeDrawer);
    document.addEventListener("cart:change", handleCartChanged);
    document.addEventListener(
      "cart-drawer:refreshed",
      handleCartDrawerRefreshed,
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initClearCartButton);
  } else {
    initClearCartButton();
  }

  function handleCartDrawerRefreshed(event) {
    const drawer = event?.target;
    if (drawer instanceof Element && drawer.matches("cart-drawer")) {
      initializeDrawer(drawer);
    }
  }

  function initializeDrawer(drawer) {
    if (!drawer || initializedDrawers.has(drawer)) {
      return;
    }

    drawer.addEventListener("click", onDrawerClick);
    initializedDrawers.add(drawer);
    if (currentCartItemCount !== null) {
      updateClearCartCounts(currentCartItemCount, drawer);
    }
  }

  function onDrawerClick(event) {
    const button = event.target.closest(CLEAR_BUTTON_SELECTOR);
    if (!button || !event.currentTarget.contains(button)) {
      return;
    }

    event.preventDefault();
    handleClearCart(button);
  }

  async function handleClearCart(button) {
    if (button.disabled || isClearingCart) {
      return;
    }

    if (!cartHasLineItems(button)) {
      return;
    }

    toggleButtonLoading(button, true);
    isClearingCart = true;
    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true }),
    );

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }),
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/clear.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          sections: sectionsToBundle.join(","),
        }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(
          responseJson?.description ||
            button.dataset.errorMessage ||
            DEFAULT_ERROR,
        );
      }

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "cart:clear",
            cart: responseJson,
          },
        }),
      );
    } catch (error) {
      console.error("Failed to clear cart", error);
    } finally {
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:loading:end", { bubbles: true }),
      );
      toggleButtonLoading(button, false);
      isClearingCart = false;
    }
  }

  function handleCartChanged(event) {
    const count = event?.detail?.cart?.item_count;
    if (typeof count !== "number") {
      return;
    }

    currentCartItemCount = count;
    updateClearCartCounts(count);
  }

  function toggleButtonLoading(button, isLoading) {
    button.disabled = isLoading;
    if (isLoading) {
      button.setAttribute("aria-busy", "true");
    } else {
      button.removeAttribute("aria-busy");
    }
  }

  function updateClearCartCounts(count, scope = document) {
    scope.querySelectorAll("[data-clear-cart-count]").forEach((element) => {
      element.textContent = `[${count}]`;
    });
  }

  function cartHasLineItems(button) {
    const drawer = button.closest("cart-drawer");
    const scope = drawer ?? document;
    return Boolean(scope.querySelector("[data-line-key]"));
  }
})();
